"use client";

import {
  findProfile,
  OPINIONS_BY_ID,
  PROFILES,
  resolveOpinions,
  ZMANIM,
} from "@kedaat/definitions";
import { formatPlainDate, formatTime, israelDateOf, ZmanimEvaluator } from "@kedaat/engine";
import { findLocality, LOCALITIES } from "@kedaat/locations";
import { useEffect, useMemo, useState } from "react";
import { useEngine } from "@/lib/hooks";
import { useSettings } from "@/lib/useSettings";
import { verifyDates } from "@/lib/verify";
import { computeDay, formatGregorian, pointFor } from "@/lib/zmanim";
import { LocalityPicker } from "./LocalityPicker";
import { ProfilePicker } from "./ProfilePicker";

/** Chametz zmanim only appear on Erev Pesach, so they are listed separately. */
const EVERYDAY = ZMANIM.filter((z) => z.group !== "pesach");
const CHAMETZ = ZMANIM.filter((z) => z.group === "pesach");

const button =
  "rounded-xl border border-line bg-card px-3 py-2 text-sm font-medium hover:bg-accent-soft";

/** A ruled blank to write on, in print and on screen alike. */
function Line({ width }: { width: string }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block border-b border-dotted border-muted align-bottom"
      style={{ width }}
    />
  );
}

/** A printable sheet for checking one profile against the luach it stands for (SPEC 10.3). */
export function VerifySheet() {
  const engine = useEngine();
  const { settings, update, ready } = useSettings();
  const [year, setYear] = useState<number | null>(null);

  useEffect(() => setYear(israelDateOf(Date.now()).year), []);

  const locality = findLocality(settings.locality) ?? LOCALITIES[0]!;
  const profile = findProfile(settings.profile) ?? PROFILES[0]!;
  const opinions = resolveOpinions(profile);

  const sheet = useMemo(() => {
    if (!engine || !ready || year === null) return null;
    const evaluator = new ZmanimEvaluator(engine.astro, pointFor(locality));
    const dayLength = (date: { year: number; month: number; day: number }) => {
      const sunrise = evaluator.evaluate(opinions.sunrise.calc, date);
      const sunset = evaluator.evaluate(opinions.sunset.calc, date);
      return sunrise === null || sunset === null ? null : sunset - sunrise;
    };
    return verifyDates(year, engine.calendar, dayLength).map((entry) => ({
      ...entry,
      day: computeDay(evaluator, engine.calendar, entry.date, profile, {}, locality.candle.minutes),
    }));
  }, [engine, ready, year, locality, profile, opinions]);

  const cell = "border border-line px-2 py-1.5 print:px-1 print:py-0.5";

  return (
    <div className="space-y-6">
      <div className="space-y-3 print:hidden">
        <h1 className="font-serif text-3xl font-bold">דף אימות</h1>
        <p className="leading-relaxed">
          דף להדפסה, לבדיקת שיטה אחת מול הלוח שהיא אמורה לייצג. בוחרים יישוב ושיטה, מדפיסים, ומשווים
          את הזמנים ללוח המקורי. מתחת לטבלה רשומות השאלות שעדיין לא הוכרעו בשיטה הזו, ובסוף יש מקום
          לאישור ולהערות.
        </p>
        <section className="grid gap-3 sm:grid-cols-2">
          <LocalityPicker
            locality={locality}
            onSelect={(l) => update(() => ({ locality: l.code }))}
          />
          <ProfilePicker
            profile={profile}
            overrideCount={0}
            onSelect={(id) => update(() => ({ profile: id, overrides: {} }))}
            onResetOverrides={() => undefined}
          />
        </section>
        {year !== null && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={button} onClick={() => setYear(year - 1)}>
              ← {year - 1}
            </button>
            <span className="min-w-16 text-center font-medium tabular-nums">{year}</span>
            <button type="button" className={button} onClick={() => setYear(year + 1)}>
              {year + 1} →
            </button>
            <button type="button" className={`${button} ms-auto`} onClick={() => window.print()}>
              הדפסה
            </button>
          </div>
        )}
      </div>

      {sheet && year !== null ? (
        <article className="space-y-6 leading-relaxed">
          <header className="space-y-1 border-b border-line pb-3">
            <h2 className="font-serif text-2xl font-bold print:text-xl">
              דף אימות · {profile.label.he}
            </h2>
            <p className="text-sm">
              {locality.nameHe} · {year} · כדעת, זמני היום בהלכה
            </p>
            <p className="text-sm text-muted">{profile.description.he}</p>
          </header>

          <section aria-labelledby="defs-title" className="space-y-2">
            <h3 id="defs-title" className="text-lg font-bold">
              1. ההגדרות
            </h3>
            <dl className="space-y-1.5 text-sm">
              {ZMANIM.map((zman) => {
                const opinion = OPINIONS_BY_ID.get(profile.opinions[zman.id])!;
                return (
                  <div key={zman.id} className="grid grid-cols-[9rem_1fr] gap-x-3 print:text-xs">
                    <dt className="font-medium">{zman.label.he}</dt>
                    <dd>
                      <span className="font-medium">{opinion.label.he}</span>
                      <span className="text-muted"> — {opinion.explanation.he}</span>
                    </dd>
                  </div>
                );
              })}
            </dl>
            <p className="text-sm text-muted">
              הדלקת נרות: {locality.candle.minutes} דקות לפני השקיעה. צאת שבת: 8.5°. הזמנים בטבלה
              מוצגים בשניות, ללא עיגול, כדי שהעיגול לא יסתיר הפרש.
            </p>
          </section>

          <section aria-labelledby="times-title" className="space-y-2">
            <h3 id="times-title" className="text-lg font-bold">
              2. הזמנים לבדיקה
            </h3>
            <div
              tabIndex={0}
              role="region"
              aria-label="זמנים לבדיקה"
              className="overflow-x-auto print:overflow-visible"
            >
              <table className="w-full border-collapse text-xs tabular-nums print:text-[7pt]">
                <thead>
                  <tr className="bg-paper">
                    <th scope="col" className={`${cell} text-start`}>
                      תאריך
                    </th>
                    {EVERYDAY.map((z) => (
                      <th key={z.id} scope="col" className={`${cell} text-center`}>
                        {z.short.he}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sheet.map(({ date, reason, day }) => (
                    <tr key={formatPlainDate(date)}>
                      <th
                        scope="row"
                        className={`${cell} whitespace-nowrap text-start font-medium`}
                      >
                        {date.day}.{date.month}
                        {reason && <span className="font-normal text-muted"> · {reason}</span>}
                      </th>
                      {EVERYDAY.map((z) => {
                        const row = day.rows.find((r) => r.zman.id === z.id);
                        return (
                          <td key={z.id} className={`${cell} text-center`}>
                            {row?.ms == null
                              ? "—"
                              : formatTime(row.ms, row.opinion.rounding, { seconds: true })}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sheet
              .filter((entry) => entry.day.info.isErevPesach)
              .map(({ date, day }) => (
                <p key={formatPlainDate(date)} className="text-sm">
                  <strong>ערב פסח, {formatGregorian(date)}:</strong>{" "}
                  {CHAMETZ.map((z) => {
                    const row = day.rows.find((r) => r.zman.id === z.id);
                    return row?.ms == null
                      ? null
                      : `${z.label.he} ${formatTime(row.ms, row.opinion.rounding, { seconds: true })}`;
                  })
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ))}
          </section>

          <section aria-labelledby="open-title" className="space-y-2">
            <h3 id="open-title" className="text-lg font-bold">
              3. מה עדיין לא הוכרע
            </h3>
            {profile.openQuestions && profile.openQuestions.length > 0 ? (
              <ol className="list-decimal space-y-2 ps-5 text-sm">
                {profile.openQuestions.map((question) => (
                  <li key={question.he}>{question.he}</li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-muted">אין שאלות פתוחות רשומות בשיטה זו.</p>
            )}
          </section>

          <section aria-labelledby="sign-title" className="space-y-3">
            <h3 id="sign-title" className="text-lg font-bold">
              4. אישור
            </h3>
            <p className="text-sm">
              הזמנים בדף זה נבדקו מול <Line width="16rem" /> ונמצאו מתאימים, או טעונים תיקון כמפורט
              להלן.
            </p>
            <div className="space-y-4 text-sm">
              <p>
                הערות ותיקונים: <Line width="60%" />
              </p>
              <Line width="100%" />
              <Line width="100%" />
              <div className="flex flex-wrap gap-x-10 gap-y-4 pt-2">
                <p>
                  שם: <Line width="12rem" />
                </p>
                <p>
                  תאריך: <Line width="8rem" />
                </p>
                <p>
                  חתימה: <Line width="12rem" />
                </p>
              </div>
            </div>
          </section>

          <footer className="border-t border-line pt-3 text-xs leading-relaxed text-muted">
            הזמנים מחושבים בספריית KosherJava ומושווים בבדיקות למנוע אסטרונומי עצמאי, בהפרש של עד 20
            שניות. פירוט מלא של כל השיטות בעמוד השיטות והחישובים באתר.
          </footer>
        </article>
      ) : (
        <div
          className="h-96 animate-pulse rounded-xl bg-card"
          aria-busy="true"
          aria-label="מחשב זמנים"
        />
      )}
    </div>
  );
}
